import Chart from 'chart.js/auto';

// Importing chart.js/auto here registers every controller/scale once for the
// whole visualisation board, so the chart components below stay declarative.
export { Chart };

/**
 * Chart tokens. The categorical order and the ordinal ramp were both checked
 * with the data-viz palette validator against the light chart surface:
 *  - categorical (5 slots): adjacent CVD dE 9.1, normal-vision dE 19.6 -> pass
 *  - ordinal blue (5 steps): monotone lightness, all dL >= 0.06, light end 2.06:1
 * Three categorical slots sit under 3:1 on white, so every chart that uses them
 * ships visible labels plus the table view (the "relief" rule).
 */
export const VIZ = {
  surface: '#ffffff',
  grid: '#eceae6',
  textPrimary: '#0b0b0b',
  textSecondary: '#52514e',
  textMuted: '#8a8984',
  // fixed order - a series keeps its hue no matter how the filter changes
  categorical: ['#2a78d6', '#eb6834', '#1baf7a', '#eda100', '#e87ba4'],
  sequential: '#2a78d6',
  ordinal: ['#86b6ef', '#5598e7', '#2a78d6', '#1c5cab', '#104281'],
  muted: '#c9c7c2',
};

// Hues used after the five validated slots run out (a metric with six or more
// series, e.g. fees split by every payment mode). They are kept apart from the
// first five in hue, and every multi-series chart ships a legend plus the table
// view, so identity never rests on colour alone.
const EXTENDED_CATEGORICAL = ['#7b5bd6', '#2fa8b8', '#a8643a', '#5d6b82', '#c23b4e', '#7fa82a', '#d65bb8'];

/**
 * Colour for the n-th series of a chart. Stable by index, so a series keeps its
 * hue as the filter moves; a dataset may still pin its own with `color`.
 */
export const seriesColor = (index, override) => {
  if (override) return override;
  const palette = [...VIZ.categorical, ...EXTENDED_CATEGORICAL];
  return palette[index % palette.length];
};

/** Legend style shared by every chart, so a colour is always named on the card. */
export const legendStyle = (overrides = {}) => ({
  display: true,
  position: 'top',
  align: 'end',
  ...overrides,
  labels: {
    boxWidth: 10,
    boxHeight: 10,
    color: VIZ.textSecondary,
    font: { size: 11 },
    ...(overrides.labels || {}),
  },
});

export const MAX_BAR_THICKNESS = 24;

export const formatNumber = (value) => Number(value || 0).toLocaleString('en-IN');

export const formatCurrency = (value) =>
  `₹${Number(value || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;

const FONT = { family: "'Inter', system-ui, sans-serif", size: 11 };

// Shared tooltip: value leads, series name follows, keyed by a line swatch.
export const tooltipStyle = (valueFormatter = formatNumber) => ({
  backgroundColor: '#141413',
  padding: 10,
  cornerRadius: 8,
  titleFont: { ...FONT, size: 11, weight: '600' },
  titleColor: '#f5f4f1',
  bodyFont: { ...FONT, size: 12, weight: '600' },
  bodyColor: '#ffffff',
  displayColors: true,
  boxWidth: 10,
  boxHeight: 2,
  usePointStyle: false,
  callbacks: {
    label: (ctx) => {
      const name = ctx.dataset?.label;
      const value = valueFormatter(ctx.parsed?.y ?? ctx.parsed?.x ?? ctx.parsed);
      return name ? `${value}  ·  ${name}` : `${value}`;
    },
  },
});

export const axisStyle = (showGrid = true) => ({
  grid: {
    display: showGrid,
    color: VIZ.grid,
    lineWidth: 1,
    drawTicks: false,
    drawBorder: false,
  },
  border: { display: false },
  ticks: { color: VIZ.textSecondary, font: FONT, padding: 8 },
});

/**
 * Draws the value at each bar's data-end. Bars carry their number directly, so
 * the low-contrast hues never have to be read as the only signal.
 */
export const valueLabelPlugin = (formatter = formatNumber) => ({
  id: 'valueLabel',
  afterDatasetsDraw(chart) {
    const { ctx } = chart;
    const horizontal = chart.options.indexAxis === 'y';
    ctx.save();
    ctx.font = `600 11px ${FONT.family}`;
    ctx.fillStyle = VIZ.textSecondary;
    chart.data.datasets.forEach((dataset, datasetIndex) => {
      const meta = chart.getDatasetMeta(datasetIndex);
      if (meta.hidden) return;
      meta.data.forEach((bar, index) => {
        const raw = dataset.data[index];
        if (raw === null || raw === undefined || raw === 0) return;
        const text = formatter(raw);
        if (horizontal) {
          ctx.textAlign = 'left';
          ctx.textBaseline = 'middle';
          ctx.fillText(text, bar.x + 6, bar.y);
        } else {
          ctx.textAlign = 'center';
          ctx.textBaseline = 'bottom';
          ctx.fillText(text, bar.x, bar.y - 6);
        }
      });
    });
    ctx.restore();
  },
});

/** Vertical hairline under the pointer, so a reader aims at a date not a line. */
export const crosshairPlugin = {
  id: 'crosshair',
  afterDatasetsDraw(chart) {
    const active = chart.tooltip?.getActiveElements?.() || [];
    if (!active.length) return;
    const { ctx, chartArea } = chart;
    const x = active[0].element.x;
    ctx.save();
    ctx.beginPath();
    ctx.moveTo(x, chartArea.top);
    ctx.lineTo(x, chartArea.bottom);
    ctx.lineWidth = 1;
    ctx.strokeStyle = VIZ.muted;
    ctx.stroke();
    ctx.restore();
  },
};

/** Semi-transparent version of a hex token, for the 10% area wash. */
export const withAlpha = (hex, alpha) => {
  const value = hex.replace('#', '');
  const r = parseInt(value.slice(0, 2), 16);
  const g = parseInt(value.slice(2, 4), 16);
  const b = parseInt(value.slice(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};
